// src/pages/MyTransactions.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";

function displayOrDash(val) {
  const s = typeof val === "string" ? val.trim() : val;
  return s ? String(s) : "-";
}

export default function MyTransactions() {
  const [batches, setBatches] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);

  const token = localStorage.getItem("accessToken");
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  useEffect(() => {
    async function fetchBatches() {
      if (!token) {
        setError("You must be logged in to view transactions.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const meRes = await fetch(`${API_BASE_URL}/api/auth/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!meRes.ok) throw new Error("Failed to load user.");

        const meData = await meRes.json();
        setUser(meData);

        const isAdmin = ["admin", "superadmin"].includes(meData?.role);
        const endpoint = isAdmin
          ? `${API_BASE_URL}/api/admin/borrow-batches/history/`
          : `${API_BASE_URL}/api/borrow-batches/my/`;

        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Failed to load borrow batches.");

        const data = await res.json();
        setBatches(Array.isArray(data) ? data : data.results || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load transactions.");
      } finally {
        setLoading(false);
      }
    }

    fetchBatches();
  }, [token]);

  const statusRank = (s) => {
    const v = String(s || "").toUpperCase();
    if (v === "PENDING") return 0;
    if (v === "APPROVED") return 1;
    if (v === "COMPLETED") return 2;
    if (v === "REJECTED") return 3;
    return 9;
  };

  const sortedBatches = [...batches].sort((a, b) => {
    const ra = statusRank(a.status);
    const rb = statusRank(b.status);
    if (ra !== rb) return ra - rb;

    // newest first if you have created_at; otherwise fallback keeps stable-ish order
    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b.created_at ? new Date(b.created_at).getTime() : 0;
    return db - da;
  });

  const statusBadge = (s) => {
    const v = String(s || "").toUpperCase();
    switch (v) {
      case "PENDING":
        return "bg-black/5 dark:bg-white/10 text-strong border border-white/40 dark:border-white/10";
      case "APPROVED":
        return "badge badge-success";
      case "COMPLETED":
        return "badge badge-info";
      case "REJECTED":
        return "badge badge-danger";
      default:
        return "bg-black/5 dark:bg-white/10 text-strong border border-white/40 dark:border-white/10";
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">
        {isAdmin ? "Batch History" : "My Transactions"}
      </h1>
      <p className="text-xs text-muted mb-4">
        One row = one batch (max 10 items).
      </p>

      {loading && <p className="text-sm text-muted">Loading…</p>}
      {error && !loading && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {!loading && !error && batches.length === 0 && (
        <p className="text-sm text-muted">
          You have not submitted any borrow requests yet.
        </p>
      )}

      {!loading && !error && batches.length > 0 && (
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Ref
                </th>
                {isAdmin && (
                  <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                    Borrower
                  </th>
                )}
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Event
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  PIC
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Items
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Tanggal peminjaman
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Expected return
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedBatches.map((b) => {
                const expanded = expandedId === b.id;

                return (
                  <React.Fragment key={b.id}>
                    <tr className="border-t">
                      <td className="px-4 py-2 text-xs font-mono text-strong">
                        {b.reference_code}
                      </td>

                      {isAdmin && (
                        <td className="px-4 py-2 text-xs text-strong">
                          {b.user || "-"}
                        </td>
                      )}

                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof b.event_name === "string" ? b.event_name : ""}
                      >
                        {displayOrDash(b.event_name)}
                      </td>
                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof b.event_pic_name === "string" ? b.event_pic_name : ""}
                      >
                        {displayOrDash(b.event_pic_name)}
                      </td>

                      <td className="px-4 py-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : b.id)}
                          className="text-brand-700 font-semibold hover:underline"
                        >
                          {expanded ? "Hide" : "Show"} ({b.items?.length || 0})
                        </button>
                      </td>

                      <td className="px-4 py-2 text-xs text-muted">
                        {b.borrow_date || "-"}
                      </td>

                      <td className="px-4 py-2 text-xs text-muted">
                        {b.expected_return_date || "-"}
                      </td>

                      <td className="px-4 py-2 text-xs">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold ${statusBadge(
                            b.status
                          )}`}
                        >
                          {b.status}
                        </span>
                      </td>

                      <td className="px-4 py-2 text-xs">
                        {["PENDING", "APPROVED"].includes(String(b.status || "").toUpperCase()) ? (
                          <Link
                            to={`/borrow-requests/print?batch=${b.id}`}
                            target="_blank"
                            className="text-brand-700 font-semibold hover:underline"
                          >
                            Print
                          </Link>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>

                    {expanded && (
                      <tr className="border-t bg-brand-500/5">
                        <td colSpan={isAdmin ? 9 : 8} className="px-6 py-3">
                          <div className="inline-block text-xs text-strong">
                            {(b.items || []).map((it) => (
                              <div
                                key={it.item}
                                className="grid grid-cols-[auto_auto] items-center gap-x-4 py-1 border-b border-white/40 dark:border-white/10 last:border-b-0"
                              >
                                <span>{it.item_name || `Item #${it.item}`}</span>
                                <span className="font-semibold text-right">{it.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}